/**
 * Property columns on the people, companies and deals lists: which ones a
 * person has added is remembered in this browser, per person and per list.
 *
 * Kept in localStorage, guarded: a private window or blocked storage just
 * means no extra columns, never a broken list.
 */
import { useMemo, useState } from 'react';
import { ActionIcon, Checkbox, Popover, ScrollArea, Stack, Table, Text, Tooltip } from '@mantine/core';
import { IconColumns3 } from '@tabler/icons-react';
import { useProperties, type PropertyDef, type PropertyObject } from '../../api/properties';
import { useNavUser } from '../../layout/navUser';
import { useVocab } from '../../vocab';
import { formatValue, isEmptyValue } from './propertyModel';

type ListObject = Exclude<PropertyObject, 'submission'>;

export function columnsStorageKey(userId: string | null | undefined, object: ListObject): string {
  return `rs.columns.${object}.${userId ?? 'anon'}`;
}

function read(key: string): string[] {
  try {
    const raw = window.localStorage.getItem(key);
    const parsed: unknown = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed.filter((x): x is string => typeof x === 'string') : [];
  } catch {
    return [];
  }
}

function write(key: string, keys: string[]) {
  try {
    window.localStorage.setItem(key, JSON.stringify(keys));
  } catch {
    // Storage blocked: the choice lasts until the page is left.
  }
}

/**
 * The property columns chosen for a list, as live definitions in the order
 * the organisation keeps them. A chosen key whose property has since been
 * archived or deleted is quietly left out.
 */
export function usePropertyColumns(object: ListObject, enabled = true) {
  const user = useNavUser();
  const key = columnsStorageKey(user?.id, object);
  const data = useProperties(object, { enabled }).data;
  const defs = useMemo(() => data?.properties ?? [], [data]);
  const [chosen, setChosenState] = useState<string[]>(() => read(key));
  const setChosen = (next: string[]) => {
    setChosenState(next);
    write(key, next);
  };
  const columns = useMemo(() => defs.filter((d) => chosen.includes(d.key)), [defs, chosen]);
  return { defs, chosen, setChosen, columns };
}

/** The button and list for adding and removing property columns. */
export function ColumnPicker({
  defs, chosen, onChange,
}: {
  defs: PropertyDef[];
  chosen: string[];
  onChange: (next: string[]) => void;
}) {
  const v = useVocab();
  if (defs.length === 0) return null;
  return (
    <Popover position="bottom-end" withinPortal shadow="md" width={260}>
      <Popover.Target>
        <Tooltip label={`Show ${v.many('property')} as columns`} withArrow>
          <ActionIcon variant="default" size="lg" aria-label="Columns">
            <IconColumns3 size={16} />
          </ActionIcon>
        </Tooltip>
      </Popover.Target>
      <Popover.Dropdown>
        <Stack gap={6}>
          <Text size="xs" fw={600} c="dimmed" tt="uppercase">{`${v.Many('property')} as columns`}</Text>
          <ScrollArea.Autosize mah={300}>
            <Stack gap={6} data-testid="column-picker">
              {defs.map((d) => (
                <Checkbox
                  key={d.key}
                  size="xs"
                  label={d.label}
                  checked={chosen.includes(d.key)}
                  onChange={(e) => onChange(
                    e.currentTarget.checked ? [...chosen, d.key] : chosen.filter((k) => k !== d.key),
                  )}
                />
              ))}
            </Stack>
          </ScrollArea.Autosize>
        </Stack>
      </Popover.Dropdown>
    </Popover>
  );
}

export function PropertyHeaders({ columns }: { columns: PropertyDef[] }) {
  return (
    <>
      {columns.map((d) => (
        <Table.Th key={d.key} ta={d.type === 'number' || d.type === 'currency' ? 'right' : undefined}>{d.label}</Table.Th>
      ))}
    </>
  );
}

export function PropertyCells({
  columns, values, names,
}: {
  columns: PropertyDef[];
  values: unknown;
  names?: Record<string, string>;
}) {
  const record = (values && typeof values === 'object' ? values : {}) as Record<string, unknown>;
  return (
    <>
      {columns.map((d) => {
        const value = record[d.key];
        const numeric = d.type === 'number' || d.type === 'currency';
        return (
          <Table.Td key={d.key} ta={numeric ? 'right' : undefined}>
            {isEmptyValue(value)
              ? <Text size="sm" c="dimmed">-</Text>
              : <Text size="sm" lineClamp={1} style={numeric ? { fontVariantNumeric: 'tabular-nums' } : undefined}>{formatValue(d, value, names)}</Text>}
          </Table.Td>
        );
      })}
    </>
  );
}
